import React, { useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const NavContainer = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: ${(props) =>
    props.scrolled
      ? "rgba(10, 10, 10, 0.9)"
      : "linear-gradient(135deg, rgba(10, 10, 10, 0.8), rgba(20, 20, 30, 0.8))"};
  backdrop-filter: blur(10px);
  z-index: 1000;
  transition: all 0.3s ease;
  border-bottom: ${(props) =>
    props.scrolled ? "1px solid rgba(255, 215, 0, 0.2)" : "none"};
`;

const NavWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  position: relative;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Logo = styled(motion.div)`
  font-size: 1.8rem;
  font-weight: bold;
  background: linear-gradient(135deg, #ffd700, #ffed4e);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  cursor: pointer;
`;

const NavMenu = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled.a`
  color: #ffffff;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    color: #ffd700;
    transform: translateY(-2px);
  }

  &:before {
    content: "";
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 0;
    height: 2px;
    background: linear-gradient(135deg, #ffd700, #ffed4e);
    transition: width 0.3s ease;
  }

  &:hover:before {
    width: 100%;
  }
`;

const LanguageButton = styled(motion.button)`
  background: rgba(255, 215, 0, 0.1);
  border: 1px solid rgba(255, 215, 0, 0.3);
  color: #ffd700;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 215, 0, 0.2);
    border-color: #ffd700;
  }
`;

const LoginButton = styled(motion.button)`
  background: linear-gradient(135deg, #ffd700, #ffed4e);
  color: #000;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(255, 215, 0, 0.3);
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: #ffd700;
  font-size: 1.5rem;
  cursor: pointer;

  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenu = styled(motion.div)`
  display: none;
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: rgba(10, 10, 10, 0.95);
  backdrop-filter: blur(10px);
  padding: 2rem;
  flex-direction: column;
  gap: 1.5rem;

  @media (max-width: 768px) {
    display: ${(props) => (props.isOpen ? "flex" : "none")};
  }
`;

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <NavContainer scrolled={scrolled}>
      <NavWrapper>
        <Link to="/" style={{ textDecoration: "none" }}>
          <Logo
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            Ferman
          </Logo>
        </Link>

        {/* Desktop Menu */}
        <NavMenu>
          <LanguageButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => alert("Persian language support coming soon!")}
          >
            🇬🇧 EN
          </LanguageButton>

          <NavLink href="#services">Services</NavLink>
          <NavLink href="#features">Features</NavLink>
          <NavLink href="#pricing">Pricing</NavLink>
          <NavLink href="/about">About</NavLink>

          <Link to="/login" style={{ textDecoration: "none" }}>
            <LoginButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Login
            </LoginButton>
          </Link>
        </NavMenu>

        {/* Mobile Hamburger Button */}
        <MobileMenuButton onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          ☰
        </MobileMenuButton>

        {/* Mobile Menu */}
        <MobileMenu
          isOpen={mobileMenuOpen}
          initial={{ opacity: 0, y: -20 }}
          animate={{
            opacity: mobileMenuOpen ? 1 : 0,
            y: mobileMenuOpen ? 0 : -20,
          }}
        >
          <LanguageButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              alert("Persian language support coming soon!");
              setMobileMenuOpen(false);
            }}
          >
            🇬🇧 EN
          </LanguageButton>
          <NavLink href="#services" onClick={() => setMobileMenuOpen(false)}>
            Services
          </NavLink>
          <NavLink href="#features" onClick={() => setMobileMenuOpen(false)}>
            Features
          </NavLink>
          <NavLink href="#pricing" onClick={() => setMobileMenuOpen(false)}>
            Pricing
          </NavLink>
          <NavLink href="/about" onClick={() => setMobileMenuOpen(false)}>
            About
          </NavLink>
          <Link to="/login" style={{ textDecoration: "none" }}>
            <LoginButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setMobileMenuOpen(false)}
            >
              Login
            </LoginButton>
          </Link>
        </MobileMenu>
      </NavWrapper>
    </NavContainer>
  );
};

export default Navigation;
